import {withCallState, withDevtools, withStorageSync,} from '@angular-architects/ngrx-toolkit';
import {
  patchState,
  signalMethod,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {pipe, tap} from 'rxjs';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {computed, inject} from '@angular/core';
import {ClientImageGraphService} from '../AIReports';
import {DeepAgentStreamState} from '@models/ai-reports/shared/deep-agent-state';

export type ChatState = {
	/**
	 * Previous prompts sent by the user.
	 */
	history: string[];
	thread_id: string;
	/**
	 * True when the current thread's last run crashed and a manual retry is pending.
	 * Hydrated from the server on thread load; toggled live by crash/retry/new-prompt.
	 */
	pending_retry: boolean;
} & Pick<DeepAgentStreamState, 'messages' | 'summary'>;

const initialState: ChatState = {
	history: [],
	messages: [],
	summary: null,
	thread_id: '',
	pending_retry: false,
};

export type ChatStore = InstanceType<typeof ChatStore>;
export const ChatStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withCallState(),
	withProps(() => ({
		graphClient: inject(ClientImageGraphService),
	})),
	withComputed((store) => ({
		lastMessage: computed(() => store.messages()?.at(-1) ?? undefined),
		/**
		 * Asset IDs belonging to the most recent AI result, sourced directly from the summary.
		 */
		resultAssets: computed(() => store.summary()?.assets ?? []),
	})),
	withMethods((store) => ({
		updateGraphState: rxMethod(
			pipe(tap((state: DeepAgentStreamState) => patchState(store, state)))
		),
		addHistoryMessage: (message: string) => {
			const history = store.history();
			if (!history.includes(message))
				patchState(store, { history: [...history, message] });
		},
		setPendingRetry: (pending: boolean) => patchState(store, { pending_retry: pending }),
		deleteHistoryMessage: (message: string) => {
			const history = store.history();
			if (history.includes(message))
				patchState(store, { history: history.filter((m) => m !== message) });
		},
		clearHistory: () => patchState(store, { history: [] }),
		setThreadId: (thread_id: string) => patchState(store, { thread_id }),
		/**
		 * And fetches the state from the server.
		 */
		selectThread: signalMethod(async (thread_id: string | undefined) => {
			if (thread_id) {
				patchState(store, { callState: 'loading' });
				try {
					const { messages, summary, pending_retry } = await store.graphClient.fetchState(thread_id);
					patchState(store, {
						thread_id,
						messages,
						summary,
						pending_retry: pending_retry ?? false,
						callState: 'loaded',
					});
				} catch (e) {
					patchState(store, { callState: { error: e } });
				}
			} else
				patchState(store, {
					thread_id,
					messages: [],
					summary: null,
					pending_retry: false,
					callState: 'loaded',
				});
		}),
	})),

	withStorageSync({
		key: 'chat',
		// images are too large and messages don't serialize well on the client side
		// TODO: assets(former images) are now not so large.
		// Exclude messages (too large) and pending_retry (always re-hydrated from the server on
		// thread load — persisting it would briefly flash a stale "Try again" bubble next session).
		select: ({ messages, pending_retry, ...rest }) => ({ ...rest }),
	}),
	withHooks((store) => ({
		onInit: async () => {
			return store.selectThread(store.thread_id());
		},
	})),

	/*	// Keeps chat state in the local storage
			//async_local_storage.js:6 Uncaught TypeError: import_node_async_hooks.AsyncLocalStorage is not a constructor
			//     at initializeAsyncLocalStorageSingleton (async_local_storage.js:6:62)
			//     at index.js:18:1
			withStorageSync(
				{
					key: 'chat',
					autoSync: false,
					/!**
					 * Function used to parse the state coming from storage.
					 *
					 * `JSON.parse()` by default
					 *!/
					parse: (stateString: string) => {
						const chatState = JSON.parse(stateString);
						return resolveStoredMessages<ChatState>(chatState);
					},
					/!**
					 * Function used to transform the state into a string representation.
					 *
					 * `JSON.stringify()` by default
					 *!/
					stringify: (state: ChatState) => JSON.stringify(storeMessages(state)),
				},
				withIndexedDB()
			),
			withHooks({
				async onInit(store) {
					// Load persisted messages on init
					await store.readFromStorage();
				},
			}),*/
	withDevtools('Chat Store')
);
